import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch, apiPatch } from '../../utils/api';
import { notify as notifyDataBus } from '../../utils/dataBus';
import { useDepartment } from '../../contexts/DepartmentContext';
import useSubmitGuard from '../../hooks/useSubmitGuard';
import useHiresInvalidation from '../../hooks/useHiresInvalidation';
import usePollingRefresh from '../../hooks/usePollingRefresh';
import { useLatestRequest } from '../../hooks/useSearchRequest';
import useHireSearch from '../../hooks/useHireSearch';
import useAdjustmentDecision from '../../hooks/useAdjustmentDecision';
import useStickyToolbar from '../../hooks/useStickyToolbar';
import useHireTableList from '../../hooks/useHireTableList';
import useHiresViewSwitch from '../../hooks/useHiresViewSwitch';
import useDeepLinkParam from '../../hooks/useDeepLinkParam';
import { useHistoryState, useOpenRowInHistory } from '../../hooks/useViewInHistory';
import { HIRE_TABLE_TABS } from '../../utils/hireBuckets';
import useColumnWidths from '../../hooks/useColumnWidths';
import useFitColumnsToScreen from '../../hooks/useFitColumnsToScreen';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';
import EmptyState from '../EmptyState';
import CostSummary from './CostSummary';
import KanbanView from './KanbanView';
import GanttView from './GanttView';
import CalendarView from './CalendarView';
import HireActionModal from './HireActionModal';
import { PRICE_CHOICE_ACTUAL } from './OffHirePriceChoice';
import HireAdjustmentDecisionDialog from './HireAdjustmentDecisionDialog';
import HireOrderExpansion from './HireOrderExpansion';
import HiresListChrome from './HiresListChrome';
import HireListActionsMenu from './HireListActionsMenu';
import { MAX_HIRE_COLUMN_WIDTH, hireTableColumnModel } from './HireTableView';
import {
  patchHireWithAdjustmentDecision,
  patchHireDrag,
  hireRefusalMessage,
} from '../../utils/hirePatchWithAdjustmentDecision';
import { buildHiresViewTabs } from '../../constants/hiresViewTabs';
import { excludeCancelledOrderHires, cancelledOrderItemIds, bucketHires, isHireOverdue } from '../../utils/hireBuckets';

// CSS for pulsing border animation (overdue items)
const pulseStyle = `
@keyframes pulse-border {
  0%, 100% { border-color: rgb(248 113 113); }
  50% { border-color: rgb(239 68 68); box-shadow: 0 0 8px rgba(239, 68, 68, 0.3); }
}
.animate-pulse-border {
  animation: pulse-border 2s ease-in-out infinite;
}
`;

/**
 * ─────────────────────────────────────────────────────────────────────────
 * THE hires page. ONE definition, parameterised by SCOPE (2026-10-03).
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Owner, 2026-10-03, asked for an audit of *"where we should/could be using
 * common code"* and then ruled *"lets do them all"*. `pages/MyHires.js` (706
 * lines) and `pages/DepartmentHires.js` (810) shared ~250 sorted-unique lines
 * and twenty-six imports; `pages/AdminHires.js` (490) carried a third copy of
 * the fetch, the search, the three money handlers and the three views with its
 * own hand-rolled toolbar. All three are now thin SCOPE descriptors handed to
 * this component.
 *
 * 🔑 **EVERY REAL DIFFERENCE BETWEEN THE THREE PAGES IS A FIELD ON THE SCOPE,
 * AND EVERY FIELD DEFAULTS TO WHAT THAT PAGE ALREADY DID.** Controller ruling,
 * 2026-10-03: *"keep each page's CURRENT behaviour as a parameter of the shared
 * body. Default to the current behaviour and do not converge anything."* So a
 * field reading `poll: false` is not a gap to close — it is `AdminHires`'s
 * shipped behaviour, held still on purpose, awaiting an owner ruling. The two
 * places a difference was genuinely UNIFIED are marked 🟰 at their field and
 * each carries the measurement that licenses it.
 *
 * 🚨 **THIS COMPONENT RESOLVES NOTHING ABOUT AUTHORITY.** `canManage` and the
 * expanded row's `deptWide` come from the scope's own functions over the
 * `DepartmentContext` slice, written where that gate already lived — the
 * contract `OrderTableView` and `OrderActionsMenu` have, for the same reason.
 *
 * ── What the three scopes are ─────────────────────────────────────────────
 * - `MY_HIRES_SCOPE`         — the caller's OWN hires, in the SELECTED
 *   department only (owner ruling 2026-09-01; memory `myhires-selected-
 *   department-only`). Self-scoped `?user_id=`, which the server then confines
 *   to the active department (`routes/hires.js#selfScopedDeptClause`).
 * - `DEPARTMENT_HIRES_SCOPE` — one department, `?department_id=`, FAIL CLOSED
 *   when none is selected.
 * - `ADMIN_HIRES_SCOPE`      — every department at once, no query at all.
 *
 * ── Rules that travelled with the code and must not be re-derived ────────
 * - The search box is CLIENT-SIDE ONLY (`hooks/useHireSearch.js`): the three
 *   endpoints are fetched ONCE, typing filters arrays already in memory, so
 *   there is no response to race. Declared CLIENT_FILTER in
 *   `hooks/useSearchRequest.staleGuardManifest.test.js` — the declaration is
 *   against THIS file now, not the three pages. `debounceMs={0}`, not
 *   persisted, never a chip.
 * - Kanban / Gantt / Calendar exclude a hire whose ORDER is cancelled or
 *   rejected (owner ruling 2026-09-28, `utils/hireBuckets.js`): a cancelled
 *   order was never hired, so it must not show as live. It appears only in the
 *   Table's Cancelled tab. Calendar events carry no `order_status` of their own
 *   (`GET /api/hires/calendar` selects a strict subset of `GET /api/hires`'s
 *   columns), so they are joined by `item_id` against `items`.
 * - A DEPARTMENT SWITCH IS A SECOND IN-FLIGHT REQUEST (review round 4): every
 *   write in `fetchData` is gated on `isCurrent()` — items, summary, calendar,
 *   the catch's error AND the finally's loading flag — so a superseded request
 *   writes nothing. `scope.staleRequestGuard` is what arms it.
 * - `hasLoaded` is "a real list is on screen": a failed SILENT refetch keeps it
 *   (the error banner sits ABOVE the still-mounted table and any open, possibly
 *   dirty, expansion — Dept Orders' shape), a failed FIRST load leaves it false
 *   (the error card alone). It is also `useHireTableList`'s `ready`, because
 *   `!loading && !error` parked a `?hire=` resolve until the error next cleared
 *   and then expanded a row with no click behind it (round-4 finding F3).
 * - The 30-second poll (`hooks/usePollingRefresh.js`) is SILENT and
 *   `suppressErrors`, and `paused` is the DIRTY GUARD plus the two money
 *   dialogs, so nothing re-buckets rows under an open editor or changes the
 *   item a typed date belongs to. Ticks are SKIPPED, never queued.
 * - 🪤 The three endpoint literals `/api/hires?`, `/api/hires/summary?` and
 *   `/api/hires/calendar?` are what `server/tests/regressions/
 *   hires-poll-exclusion.test.js` §4 scans for. Keep them as literal template
 *   heads; do not refactor them into one interpolated base.
 *
 * ── 🔴 Admin Hires gained the shared chrome and the Table view (item B) ───
 * See `ADMIN_HIRES_SCOPE`'s own docblock for what changed, what was held
 * still, and the measurement that falsified the "in-place opening needs a
 * server change" hypothesis.
 */

export default function HiresPage({ scope }) {
  const navigate = useNavigate();
  const { isAdmin, activeDepartment, capabilitiesFor, loading: deptLoading } = useDepartment();
  const ctx = { isAdmin, activeDepartment, capabilitiesFor };
  /**
   * 🚨 **THE CONTEXT SLICE GOES IN A REF, NEVER IN A DEPENDENCY ARRAY, AND THIS
   * IS A MEASURED BUG NOT A STYLE CHOICE.** `useDepartment()` hands back a fresh
   * object with a fresh `capabilitiesFor` closure on most renders, so a
   * `useCallback` that lists the slice is a NEW FUNCTION every render — and
   * `fetchData` is in a `useEffect`'s dependency array, so that is an infinite
   * fetch → setState → render → fetch loop. Caught by
   * `MyHires.adjustmentDecision.test.js` timing out at 5s during this
   * extraction; the three pages it replaced each depended only on PRIMITIVES
   * (`activeDepartmentId`, `canQuery`, `beginFetch`) and so never had it.
   * Every callback below therefore reads `ctxRef.current` and depends on the
   * primitives derived from the slice, never on the slice itself.
   */
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  /**
   * View tab state. `scope.initialView` resolves it ONCE at mount, like the
   * order pages — a resize mid-session does not switch view.
   *
   * 🔑 An explicit SAVED choice (`sfx_hires_view`) always wins on every scope,
   * which is the whole reason Admin Hires could not keep its own three-id
   * validator: that key is shared by all three pages, so a stored `'table'`
   * silently fell back to Kanban THERE while being honoured on the other two.
   * What the scope still owns is the default when nothing is saved — TABLE on
   * desktop / Kanban on a phone for My and Department Hires (owner ruling
   * 2026-09-27), and Kanban for Admin Hires, which is where it has always
   * opened. Held, per the controller's "default to current behaviour".
   */
  const [activeTab, setActiveTab] = useState(scope.initialView);
  const [calendarEvents, setCalendarEvents] = useState([]);

  const { searchTerm, setSearchTerm, filteredItems, filteredEvents, searchHidEverything } =
    useHireSearch(items, calendarEvents);

  const liveItems = useMemo(() => excludeCancelledOrderHires(filteredItems), [filteredItems]);
  const cancelledIds = useMemo(() => cancelledOrderItemIds(items), [items]);
  // The banner's four boxes count the SAME buckets as the Kanban columns and
  // the Table tabs (owner 2026-10-08: On Hire 11 in a box beside an On Hire
  // column of 10 read as a mistake). Over the UNFILTERED list — the banner
  // never moves with the search box — so with no search they match exactly.
  const bannerCounts = useMemo(() => {
    const b = bucketHires(excludeCancelledOrderHires(items));
    return {
      upcoming: b.upcoming.length,
      onHire: b.onHire.length,
      endingSoon: b.endingSoon.length,
      overdue: b.endingSoon.filter(isHireOverdue).length,
      offHired: b.offHired.length,
    };
  }, [items]);
  const liveEvents = useMemo(
    () => filteredEvents.filter((e) => !cancelledIds.has(e.item_id)),
    [filteredEvents, cancelledIds]
  );

  // Below `md` (768px) — THE order pages' live viewport flag
  // (`hooks/useIsMobileOrderViewport.js`), not an inline media query (2026-10-01).
  // Hides Gantt (a horizontal timeline does not work at 360px) and switches the
  // Table to its PHONE column set and chrome. `scope.hideGanttOnPhone` is false
  // for Admin Hires, which has never had a mobile fallback.
  const isMobile = useIsMobileOrderViewport();
  // On mobile, fall back to Kanban if the stored preference is Gantt. `activeTab`
  // + localStorage stay untouched so the desktop preference is preserved.
  const effectiveTab = (isMobile && scope.hideGanttOnPhone && activeTab === 'gantt') ? 'kanban' : activeTab;

  // ── The Table's list state — tab, sort, page, page size, the dirty-row pin
  // and the `?hire=` resolve — `hooks/useHireTableList.js`. `dirtyItemId` is
  // reported up by the row expansion.
  const [dirtyItemId, setDirtyItemId] = useState(null);
  // The sticky chrome's measured offset — the Table's column header pins
  // beneath the TOOLBAR (never a constant: the bar wraps on a narrow screen).
  const { toolbarRef, headerTop } = useStickyToolbar();

  /**
   * ── User-draggable column widths, LIFTED to the page (2026-09-28) ─────
   *
   * `hooks/useColumnWidths.js` used to be called INSIDE `HireTableView.js`; it
   * is called here for the same reason the order pages own it: `Fit columns to
   * screen` is an `Actions ▾` item up in `HiresListChrome`, and a second route
   * into one piece of state is how one width model quietly becomes two.
   *
   * 🔑 The column SET and its storage key are both derived from
   * `scope.showRequestedBy` through `hireTableColumnModel`, so the four named
   * constants are never spelled out here — My Hires' subset set cannot be given
   * the department table's key by a typo. Both hooks are always called (Hooks
   * rules); the ACTIVE one is what the table and `Fit columns to screen` both
   * read, so they can never mean different sets.
   */
  const desktopHireModel = hireTableColumnModel({ phone: false, showRequestedBy: scope.showRequestedBy });
  const desktopHireWidths = useColumnWidths(desktopHireModel.columns, desktopHireModel.storageKey, MAX_HIRE_COLUMN_WIDTH);
  const phoneHireModel = hireTableColumnModel({ phone: true, showRequestedBy: scope.showRequestedBy });
  const phoneHireWidths = useColumnWidths(phoneHireModel.columns, phoneHireModel.storageKey, MAX_HIRE_COLUMN_WIDTH);
  const activeHireColumns = isMobile ? phoneHireModel.columns : desktopHireModel.columns;
  const {
    widths: hireColumnWidths,
    setWidth: setHireColumnWidth,
    resetColumn: resetHireColumnWidth,
  } = isMobile ? phoneHireWidths : desktopHireWidths;
  const [hireTableRegionWidth, setHireTableRegionWidth] = useState(null);
  const hireColumnsFit = useFitColumnsToScreen({
    columns: activeHireColumns,
    widths: hireColumnWidths,
    setWidth: setHireColumnWidth,
    maxWidth: MAX_HIRE_COLUMN_WIDTH,
    available: hireTableRegionWidth,
  });

  const [expandItem, setExpandItem] = useState(null);
  const clearExpandItem = useCallback(() => setExpandItem(null), []);
  const clearSearch = useCallback(() => setSearchTerm(''), [setSearchTerm]);
  // The Table's status tab lives in the history entry (2026-10-13): a browser
  // Back returns to the tab you were on. Not stored; a fresh visit opens on All.
  const [hireTab, setHireTab] = useHistoryState('hires-tab', 'all', (v) => HIRE_TABLE_TABS.some((t) => t.id === v));
  const hireTabState = useMemo(() => [hireTab, setHireTab], [hireTab, setHireTab]);
  const list = useHireTableList({
    tabState: hireTabState,
    items: filteredItems,
    allItems: items,
    searchTerm,
    clearSearch,
    isMobile,
    dirtyItemId,
    // 🔻 F3 (round-4 review, REPRODUCED): `hasLoaded`, never `!error` — see the
    // module docblock for the parked-`?hire=`-resolve mechanism.
    ready: !loading && hasLoaded && effectiveTab === 'table',
    onReveal: setExpandItem,
  });
  // The Table still holds rows (the pinned DIRTY line survives a search that
  // hides everything): never swap it for an EmptyState, or the edit unmounts —
  // Dept Orders' `&& paginatedOrders.length === 0`, the same narrowing.
  const tableHoldsRows = effectiveTab === 'table' && list.pageRows.length > 0;

  // ?hire=<item_id> — the deep link (`hooks/useDeepLinkParam.js` captures it and
  // STRIPS it from the URL at once, as for `?order=`). It opens the TABLE view
  // for this visit (the saved `sfx_hires_view` is NOT rewritten — a link is not
  // a preference) and hands the id to the list, which finds its tab and page and
  // expands the row once the hires have loaded.
  // The view strip, and "open this hire" (the deep link, and a hire clicked in
  // Kanban / Gantt / Calendar): `hooks/useHiresViewSwitch.js`. `guardRef` is
  // filled by the row expansion's `continueWhenClean` (a dirty Table row ASKS —
  // the editor's three-way prompt — then switches; 2026-09-28). The same
  // `continueWhenClean` fronts the status tabs and the Table's "View order".
  // `openItemId`: the hire the Table has open, kept across views.
  const hasHire = useCallback((id) => items.some((h) => h.item_id === id), [items]);
  const {
    guardRef: viewSwitchGuardRef, continueWhenClean, onViewChange, openHire, openItemId, onExpandedItemChange,
  } = useHiresViewSwitch({
    effectiveTab, setActiveTab, requestItem: list.requestItem, hasItem: hasHire,
  });
  useDeepLinkParam('hire', openHire);
  // A browser Back/Forward reopens the hire this entry had open in the Table
  // (2026-10-13), through the same `openHire` the `?hire=` link uses. Clean
  // rows only: see `useOpenRowInHistory`.
  useOpenRowInHistory('hires-open', effectiveTab === 'table' ? openItemId : null, openHire);

  // BACK RETURNS TO THE VIEW A HIRE WAS FOLLOWED FROM (owner 2026-10-08:
  // "pressing back on the browser does not take us back to the kanban or
  // gannt chart"). A card click in Kanban / Gantt / Calendar PUSHES one history
  // entry (same URL, `state.hiresFrom` = the view it left); Back pops it and
  // this effect restores that view, Forward re-applies the Table. Only the
  // card-click JUMP pushes — a view-strip click and the `?hire=` deep link
  // do not (Back through every tab click would be worse), and none of it
  // writes `sfx_hires_view`: following a hire is not a choice of view.
  const location = useLocation();
  const followHireFromCard = useCallback((itemOrId) => {
    navigate(`${location.pathname}${location.search}`, { state: { hiresView: 'table', hiresFrom: effectiveTab } });
    openHire(itemOrId);
  }, [navigate, location.pathname, location.search, effectiveTab, openHire]);
  const lastHistoryState = useRef(location.state);
  useEffect(() => {
    const prev = lastHistoryState.current;
    const cur = location.state;
    lastHistoryState.current = cur;
    const target = (cur && cur.hiresView) || (prev && prev.hiresFrom) || null;
    if (!target) return;
    // 🚨 Leaving the Table unmounts an open order form, so a DIRTY row ASKS
    // first, exactly as a view-strip click does (review of e3963a92: Back from
    // a dirty row silently dropped the edit — `useBlocker` cannot catch it,
    // the entry has the same pathname). Save / Discard → the view changes;
    // Keep editing → the Table stays (the history entry has already moved).
    const apply = () => setActiveTab(target);
    if (effectiveTab === 'table' && target !== 'table') continueWhenClean(apply);
    else apply();
    // Keyed on the history ENTRY: a re-render must never re-apply it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Modal state
  const [modalItem, setModalItem] = useState(null);
  const [modalAction, setModalAction] = useState(null);
  // The off-hire pane's pre-filled return date — set ONLY by
  // `openOffHireModal(item, returnDate)`, which also clears it. See there.
  const [modalReturnDate, setModalReturnDate] = useState(null);
  const [modalPriceChoice, setModalPriceChoice] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Keep-or-remove adjustment dialog state (Task 8) — `onNeedsDecision` is
  // handed to `patchHireWithAdjustmentDecision` at every
  // extend/shorten/change-start call site: opening it shows the dialog and
  // returns a promise that resolves once the human picks Keep / Remove /
  // Cancel, which the wrapper turns into a single resend (or none, on Cancel).
  // The handshake is shared — `hooks/useAdjustmentDecision.js`.
  const {
    pendingAdjustments,
    onNeedsDecision,
    decide: handleAdjustmentDecision,
  } = useAdjustmentDecision();

  /**
   * ── Double-submit guard (2026-08-13, `hooks/useSubmitGuard.js`) ────────
   *
   * The hook is ALWAYS called (Hooks rules); `scope.submitGuard` decides
   * whether the four PATCH handlers actually route through it. Department Hires
   * has guarded them since 2026-08-13 — `modalLoading` is state set before the
   * await, which is the fast-double-click hole on a Confirm button, and the
   * guard's REF drops the concurrent duplicate synchronously. My Hires and
   * Admin Hires have never carried it.
   *
   * 🔴 HELD AS-IS BY CONTROLLER RULING, NOT KEPT OPEN BY OVERSIGHT. Arming it
   * for all three is a one-field change and is my recommendation, but it is a
   * behaviour change on two pages and belongs to the owner.
   */
  const { guard: guardHiresPatch } = useSubmitGuard();
  const runGuarded = useCallback(
    (fn) => (scope.submitGuard ? guardHiresPatch(fn) : fn()),
    [scope.submitGuard, guardHiresPatch]
  );

  /** May this caller Extend / Off-hire / Bring-back-on-hire on THIS page? */
  const canManage = scope.resolveCanManage(ctx);
  /**
   * Which ORDER surface does a Table row expand into, and where does "View
   * order" go? `true` → the Department Orders panel, `false` → My Orders'.
   */
  const deptWide = scope.resolveDeptWide(ctx);
  /**
   * The viewer's OWN sub-department, on a department-wide hires list only —
   * its rows get a slate accent bar (owner 2026-10-09, for a sub-department
   * lead on Dept Hires: "colour … hires that are in my sub department", then
   * chose the accent bar over a green chevron, which already means "yours to
   * action"). Empty on My Hires (every row is the viewer's own) and for a seat
   * with no sub-department. A presentation hint, never a filter or a gate.
   */
  const ownSubDepartment = deptWide && activeDepartment && !activeDepartment.production_is_personal
    ? (activeDepartment.sub_department_name || '')
    : '';

  /**
   * May this render issue a hire request at all? FAIL CLOSED where the scope
   * says so: "no department selected" must not read as "every department",
   * which is exactly what an absent `department_id` parameter produces (the
   * server answers it with the caller's whole membership).
   */
  const canQuery = scope.resolveCanQuery(ctx);

  /**
   * 🔴 DOES CLICKING A HIRE CARD OPEN THE HIRE, OR THE ORDER?
   *
   * On My Hires and Department Hires a click on the card body calls
   * `onOpenHire` — it switches to the Table and expands that hire (owner,
   * 2026-09-27: *"it should take us to the table view in hires? and work!"*).
   * `components/hires/HireCard.js` falls back to `onViewOrder` when no
   * `onOpenHire` is given, and **Admin Hires has never passed one**, so a card
   * click there NAVIGATES TO THE ORDER instead. That divergence is pinned by
   * `AdminHires.viewOrder.test.js`.
   *
   * It is HELD as a parameter rather than converged, per the controller's
   * ruling, and the Table is still reachable on Admin Hires through the view
   * strip. My recommendation is `true` for all three — it is the owner's own
   * ruling for the other two and a card click meaning two different things on
   * two hires pages is the divergence this whole change exists to remove.
   * Owner ruling owed.
   */
  const onOpenHireFromCard = scope.openHireFromCard ? followHireFromCard : undefined;
  const openHireItemId = scope.openHireFromCard ? openItemId : null;
  /**
   * The value whose change must re-fetch, in the EFFECT's dependency array.
   *
   * 🪤 This is not redundant with `fetchData`'s own identity and dropping it
   * breaks My Hires specifically. `DEPARTMENT_HIRES_SCOPE`'s query closes over
   * the active department id, so its `fetchData` is a new closure on a switch
   * and the effect refires anyway; `MY_HIRES_SCOPE`'s reads `sfx_user_id` from
   * storage at CALL time and closes over nothing, so without this key a
   * department switch would never refetch — which is how `MyHires.js` has
   * always been wired (its effect carried `activeDepartment`).
   */
  const fetchKey = scope.resolveFetchKey(ctx);

  const beginFetch = useLatestRequest();
  const fetchData = useCallback(async ({ silent = false, suppressErrors = false } = {}) => {
    if (!canQuery) return;
    // 🪤 The scope decides whether the stale-request guard is ARMED. Admin
    // Hires has never carried one, so its `isCurrent` is a constant `true` and
    // every write lands exactly as it does today — rather than this component
    // quietly giving that page a guard nobody ruled on.
    const isCurrent = scope.staleRequestGuard ? beginFetch() : () => true;
    try {
      if (!silent) { setLoading(true); setHasLoaded(false); }
      if (!suppressErrors) setError(null);

      // 🪤 `alwaysQueryString` is not cosmetic. My Hires and Department Hires
      // have always sent a `?` even when their one parameter is absent (an
      // unseated admin on Department Hires; a browser with no `sfx_user_id`),
      // and both `*.departmentScope.test.js` suites key their fixtures on the
      // `'/api/hires?'` prefix. Admin Hires has always sent the BARE path. Both
      // shapes are preserved exactly rather than normalised — the
      // normalisation would be observable in a request log and buys nothing.
      const qs = scope.buildQueryString(ctxRef.current);
      const [hiresUrl, summaryUrl, calendarUrl] = scope.alwaysQueryString
        ? [`/api/hires?${qs}`, `/api/hires/summary?${qs}`, `/api/hires/calendar?${qs}`]
        : ['/api/hires', '/api/hires/summary', '/api/hires/calendar'];

      const [hiresRes, summaryRes, calendarRes] = await Promise.all([
        apiFetch(hiresUrl),
        apiFetch(summaryUrl),
        apiFetch(calendarUrl),
      ]);

      if (!hiresRes.ok) throw new Error(`Failed to load hires: ${hiresRes.status}`);
      if (!summaryRes.ok) throw new Error(`Failed to load summary: ${summaryRes.status}`);
      // 🔴 F8 (silent-failure survey) landed on DEPARTMENT HIRES ONLY and is
      // held that way here. On that page a refused CALENDAR response throws
      // like the other two, because an empty off-hire/return calendar reads as
      // "nothing is due back" — a confident false claim on a surface whose
      // whole job is deadlines. My Hires and Admin Hires still SWALLOW it and
      // render an empty calendar with `error` null. Spreading F8 is my
      // recommendation and is an owner ruling, not a tidy-up.
      if (scope.throwOnCalendarError && !calendarRes.ok) {
        throw new Error(`Failed to load calendar: ${calendarRes.status}`);
      }

      const hiresData = await hiresRes.json();
      const summaryData = await summaryRes.json();
      const calendarData = calendarRes.ok ? await calendarRes.json() : { events: [] };

      if (!isCurrent()) return;
      setItems(hiresData.items || []);
      setSummary(summaryData);
      setCalendarEvents(calendarData.events || []);
      setHasLoaded(true);
    } catch (err) {
      // A background poll's failure is SILENT (`hooks/usePollingRefresh.js`
      // rule 4): no banner over good rows the user did not ask to reload.
      if (suppressErrors || !isCurrent()) return;
      console.error(`[${scope.logLabel}] Fetch error:`, err);
      setError(err.message || 'Failed to load hire data');
    } finally {
      // Unconditional on `silent`: a SILENT request that superseded a
      // non-silent one is the one that must clear its spinner.
      if (isCurrent()) setLoading(false);
    }
    // 🪤 PRIMITIVES ONLY (plus the frozen module-level `scope`). `fetchKey` is
    // here rather than in the effect below so the effect's own array can stay
    // minimal: a department switch moves `fetchKey`, which re-creates
    // `fetchData`, which refires the effect. See `ctxRef` above for why the
    // context slice must never appear here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beginFetch, canQuery, fetchKey, scope]);

  useEffect(() => {
    if (!deptLoading) {
      fetchData();
    }
  }, [deptLoading, fetchData]);

  // Cross-tab live refresh: another tab edited a hire → silently re-fetch.
  // The scope supplies the department this page is scoped BY, or `null` for a
  // page that shows every department (`hooks/useHiresInvalidation.js` then
  // accepts every event). My Hires deliberately passes `null` too: `fetchData`
  // is itself the department filter (it always re-fetches the caller's CURRENT
  // active department), so an event for another department costs one wasted
  // silent refetch, never a wrong result.
  const silentRefetch = useCallback(() => fetchData({ silent: true }), [fetchData]);
  useHiresInvalidation(scope.resolveInvalidationDepartmentId(ctx), silentRefetch);

  /**
   * After an Extend / Off hire / Bring back (and their adjustment dialogs): a
   * SILENT refetch (owner, 2026-09-27: no full reload — no spinner, the table
   * stays mounted and an open expansion stays open), and in the Table the row
   * stays put if the action moved it out of the tab (`useHireTableList`'s
   * `holdRow`, Dept Orders' sticky row).
   *
   * 🔴 `scope.holdRowAfterAction` is false for Admin Hires, which has no Table
   * today beyond this change and whose handlers call a plain silent refetch.
   * With the Table now offered there, NOT pinning the row means an off-hired
   * line can jump tabs under the user's cursor — my recommendation is to arm
   * it, and it is an owner ruling.
   */
  const { holdRow } = list;
  const refreshAfterAction = useCallback(async (item) => {
    if (scope.holdRowAfterAction && effectiveTab === 'table') holdRow(item);
    await fetchData({ silent: true });
  }, [scope.holdRowAfterAction, effectiveTab, holdRow, fetchData]);

  /**
   * 🔄 THE 30-SECOND AUTO-REFRESH (2026-09-27) — `hooks/usePollingRefresh.js`
   * (`POLL_INTERVAL_MS`), in every view: silent, `suppressErrors`,
   * visibility-paused and in-flight-guarded by the hook. `paused` is the DIRTY
   * GUARD plus the two money dialogs.
   *
   * 🔴 `enabled: scope.poll` is FALSE for Admin Hires, which has never polled.
   * Arming it would also owe `POLL_ENDPOINTS` nothing new (the same three paths
   * are already declared) but it is a behaviour change on a live page, so it is
   * an owner ruling. `hooks/CLAUDE.md`'s "a NEW caller owes each polled
   * endpoint a `POLL_ENDPOINTS` entry" is satisfied either way here.
   */
  usePollingRefresh(
    useCallback(() => fetchData({ silent: true, suppressErrors: true }), [fetchData]),
    {
      enabled: scope.poll,
      paused: dirtyItemId !== null || modalItem !== null || pendingAdjustments !== null,
    }
  );

  /**
   * Deep-links into an ORDER surface with the row auto-expanded, rather than
   * `/orders/:id`'s separate page. Owner, 2026-09-01: *"always default to dep
   * hire for buyer and supervisors even if they placed the order"*.
   *
   * 🔑 **THE DESTINATION IS DECIDED BY THE VIEWER'S CAPABILITY, NEVER BY
   * WHETHER THEY RAISED THE ORDER** — the scope's own `viewOrderPath`, which
   * for My Hires reads the same `canViewDepartmentOrders` the rest of that page
   * reads, and for the two wide pages is unconditionally Department Orders.
   * `/orders/:id` is never a destination from any of the three.
   *
   * 🔀 Declared ABOVE the money handlers: they close over it in their
   * `useCallback` dependency ARRAYS, which are evaluated IMMEDIATELY at render
   * time (unlike the callback bodies), so referencing a `const` declared
   * further down throws `ReferenceError: Cannot access 'handleViewOrder' before
   * initialization`.
   */
  const handleViewOrder = useCallback((orderId) => {
    if (!orderId) return;
    navigate(scope.viewOrderPath(orderId, ctxRef.current));
  }, [navigate, scope]);

  /** The `hires-changed` payload. The one subscriber accepts a null id. */
  const notifyHiresChanged = useCallback((reason) => {
    notifyDataBus('hires-changed', { departmentId: scope.resolveNotifyDepartmentId(ctxRef.current), reason });
  }, [scope]);

  /**
   * `options.viewOrder` (2026-09-02, hire extend/shorten/change-start dialog
   * spec) — set when the money dialog's alt button ("Extend & view order") was
   * clicked. Navigation happens ONLY after `res.ok` and cleanup, inside the try
   * block — ruling 4: the apply must succeed before navigating, or a failed
   * change lands the user on the order believing it worked.
   *
   * 🚨 THE 409-CANCEL FIX (spec §0.7/§6.4): a cancelled keep/remove decision
   * throws an Error whose `.message` is the raw token `'adjustments_exist'`; it
   * used to go straight into `alert()`. Recognised and swallowed silently — no
   * alert, no navigation, no state change. `modalItem` stays set, so the date
   * form is still showing with the staged date intact (this surface's existing
   * "safe landing"). Any OTHER thrown message is a real failure and alerts.
   */
  const handleExtend = useCallback((data, options) => runGuarded(async () => {
    if (!modalItem) return;
    const orderId = modalItem.order_id;
    try {
      setModalLoading(true);
      const res = await patchHireWithAdjustmentDecision(apiPatch, `/api/hires/${modalItem.item_id}/extend`, data, { onNeedsDecision });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(hireRefusalMessage(err, 'Failed to extend hire'));
      }
      setModalItem(null);
      setModalAction(null);
      notifyHiresChanged('extend');
      await refreshAfterAction(modalItem);
      if (options && options.viewOrder) handleViewOrder(orderId);
    } catch (err) {
      if (err.message === 'adjustments_exist') return; // user cancelled keep/remove — silent
      alert(err.message);
    } finally {
      setModalLoading(false);
    }
  }), [modalItem, refreshAfterAction, onNeedsDecision, handleViewOrder, notifyHiresChanged, runGuarded]);

  // Off-hire — forwards the modal's { return_date, adjustment? } payload
  // verbatim (Task 7); it used to send `{}`. Deliberately NOT through
  // `patchHireWithAdjustmentDecision`: off-hire moves no PLANNED date, so the
  // server never 409s it that way (`pages/hiresRecomputeCallSites.manifest.test.js`).
  const handleOffHire = useCallback((data) => runGuarded(async () => {
    if (!modalItem) return;
    try {
      setModalLoading(true);
      const res = await apiPatch(`/api/hires/${modalItem.item_id}/off-hire`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(hireRefusalMessage(err, 'Failed to mark off-hire'));
      }
      setModalItem(null);
      setModalAction(null);
      notifyHiresChanged('off-hire');
      await refreshAfterAction(modalItem);
    } catch (err) {
      alert(err.message);
    } finally {
      setModalLoading(false);
    }
  }), [modalItem, refreshAfterAction, notifyHiresChanged, runGuarded]);

  const handleReHire = useCallback(() => runGuarded(async () => {
    if (!modalItem) return;
    try {
      setModalLoading(true);
      const res = await apiPatch(`/api/hires/${modalItem.item_id}/re-hire`, {});
      if (!res.ok) {
        const err = await res.json();
        throw new Error(hireRefusalMessage(err, 'Failed to bring back on hire'));
      }
      setModalItem(null);
      setModalAction(null);
      notifyHiresChanged('re-hire');
      await refreshAfterAction(modalItem);
    } catch (err) {
      alert(err.message);
    } finally {
      setModalLoading(false);
    }
  }), [modalItem, refreshAfterAction, notifyHiresChanged, runGuarded]);

  const openExtendModal = useCallback((item) => {
    setModalItem(item);
    setModalAction('extend');
  }, []);

  /**
   * `returnDate` (optional, owner ruling 2026-10-05 "Point them to Off hire"):
   * the Gantt's past-date shorten hands the dragged date here so the off-hire
   * pane opens pre-filled with it (`HireActionModal`'s `initialReturnDate`).
   * Only a `YYYY-MM-DD` string is taken — every other caller passes the item
   * alone, and anything else (a stray event) means "no prefill", as before.
   *
   * ⚖️ AND FROM THAT ROUTE ONLY the price choice opens on "Use actual"
   * (`PRICE_CHOICE_ACTUAL`) — owner ruling 2026-10-05, option A, after a
   * worked example: *"shortening a date and offhireing should both change the
   * price"*. The buyer was trying to SHORTEN, i.e. to reprice; the off-hire
   * dialog still shows the difference and the choice stays theirs. Every
   * OTHER Off hire (single and Off hire all) keeps "Keep original" as its
   * default — the 2026-09-01 ruling (*"an off-hire date is evidence about
   * paperwork, not money"*) still governs that, so OFF-HIRE NEVER
   * AUTO-REPRICES is unchanged.
   */
  const openOffHireModal = useCallback((item, returnDate) => {
    const prefill = typeof returnDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(returnDate) ? returnDate : null;
    setModalReturnDate(prefill);
    setModalPriceChoice(prefill ? PRICE_CHOICE_ACTUAL : null);
    setModalItem(item);
    setModalAction('off-hire');
  }, []);

  const openReHireModal = useCallback((item) => {
    setModalItem(item);
    setModalAction('re-hire');
  }, []);

  const closeModal = useCallback(() => {
    if (!modalLoading) {
      setModalItem(null);
      setModalAction(null);
    }
  }, [modalLoading]);

  /**
   * The Gantt drag, through the ONE shared route map
   * (`utils/hirePatchWithAdjustmentDecision.js#patchHireDrag`): `change_start` →
   * `/change-start` `{ new_hire_from }`, `extend` → `/extend` `{ new_hire_to }`,
   * anything else → `/shorten` `{ new_hire_to }`, each through the 409 wrapper,
   * each throwing `hireRefusalMessage(err, <its own fallback>)`.
   *
   * ✅ This closes the helper's own OWED note — *"`DepartmentHires.js` and
   * `MyHires.js` still carry their own (correct) inline copies — owed: adopt
   * this helper there"* — now that all three pages are one body. The three
   * branches, three payload keys and three fallback strings are byte-equivalent
   * to the copies they replace; proven by mutation rather than asserted.
   *
   * 🪤 The re-throw is UNCONDITIONAL and must stay so: `GanttView`'s own
   * `handleConfirm` keeps the money dialog OPEN on ANY throw, which is exactly
   * the "silent return to the still-open money dialog" a 409-cancel wants on
   * this surface. Only the raw-token alert is skipped.
   */
  const handleDragComplete = useCallback((item, newDateStr, action, options) => runGuarded(async () => {
    try {
      await patchHireDrag(apiPatch, item, newDateStr, action, { onNeedsDecision });
      notifyHiresChanged(action);
      await refreshAfterAction(item);
      if (options && options.viewOrder) handleViewOrder(item.order_id);
    } catch (err) {
      if (err.message !== 'adjustments_exist') alert(err.message);
      throw err; // Re-throw so GanttView keeps the dialog open
    }
  }), [refreshAfterAction, onNeedsDecision, handleViewOrder, notifyHiresChanged, runGuarded]);

  if (deptLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  /**
   * FAIL CLOSED. A non-admin standing in no department gets an explicit empty
   * state rather than a request.
   *
   * 🔑 IT MUST SAY SO, not merely render an empty list. "This department has no
   * hires" and "no department is selected" are different claims, and an empty
   * list asserts the first one falsely.
   */
  if (!canQuery) {
    return (
      <div className={scope.rootClassName} data-testid={scope.testId}>
        <EmptyState
          icon="🏢"
          title="No department selected"
          description="Choose a production and department from the switcher to see its hires."
          testId="no-active-department"
        />
      </div>
    );
  }

  const costBanner = <CostSummary summary={summary} counts={bannerCounts} loading={false} />;

  return (
    <div className={scope.rootClassName} data-testid={scope.testId}>
      <style>{pulseStyle}</style>

      {/* Error state — ABOVE a loaded list (a failed SILENT refetch must not
          unmount the table or an open, possibly dirty, expansion: Dept Orders'
          shape), alone only when nothing has loaded. Arrow-wrapped so the click
          event is never read as fetch options; the retry is silent over a
          loaded list for the same unmount reason. */}
      {error && (
        <div className="mb-4">
          <ErrorMessage error={error} onRetry={() => fetchData({ silent: hasLoaded })} />
        </div>
      )}

      {loading ? (
        <LoadingSpinner message={scope.loadingMessage} />
      ) : hasLoaded ? (
        <>
          {/* The shared two-band chrome (`components/hires/HiresListChrome.js`):
              header band, then the sticky bar — search, the view strip and, in
              TABLE view, the status tabs with the three money figures
              right-aligned beside them (owner, 2026-09-27). Every other view
              keeps the full `CostSummary` banner above the bar.
              🔑 DELIBERATELY UNFILTERED, and `summary` comes from
              `/api/hires/summary` rather than being derived from `items`: the
              search narrows what you are LOOKING AT, never what is OWED. */}
          <HiresListChrome
            toolbarRef={toolbarRef}
            contextLabel={scope.contextLabel}
            shownCount={effectiveTab === 'table' ? list.rows.length : liveItems.length}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            viewTabs={buildHiresViewTabs(isMobile && scope.hideGanttOnPhone, true)}
            activeView={effectiveTab}
            onViewChange={onViewChange}
            list={list}
            onStatusTabChange={(id) => continueWhenClean(() => list.setTab(id))}
            summary={summary}
            actions={effectiveTab === 'table' ? (
              <HireListActionsMenu
                list={list}
                searchTerm={searchTerm}
                headerTitle={scope.actionsHeaderTitle}
                departmentName={activeDepartment?.department_name || ''}
                fit={hireColumnsFit}
              />
            ) : null}
            // 🔴 My Hires drops the banner on a PHONE in every view, where
            // Department Hires drops it in TABLE view only. Held as-is.
            aboveToolbar={effectiveTab === 'table' || (isMobile && scope.hideCostBannerOnPhone) ? null : costBanner}
            phone={isMobile}
            showPhoneSummary={scope.showPhoneSummary}
          />

          {/* View content */}
          {items.length === 0 && effectiveTab !== 'calendar' && !tableHoldsRows ? (
            <EmptyState
              size="md"
              title={scope.emptyTitle}
              description={scope.emptyDescription}
            />
          ) : searchHidEverything && effectiveTab !== 'calendar' && !tableHoldsRows ? (
            /* The list HAS hires; this search matched none of them — a
               different, smaller card, never the "no hires yet" copy, which
               would be a confident false claim. The calendar is excluded on the
               same condition as the true-empty state above it: its month grid
               is worth rendering empty, because paging months is how you find
               the hire you are looking for. */
            <EmptyState
              size="sm"
              title={`No hires match “${searchTerm.trim()}”`}
              description="Clear the search to see all hires."
              testId="hires-search-empty"
            />
          ) : effectiveTab === 'table' ? (
            /* A row expands into its order — the SAME surface `handleViewOrder`
               would open: the Department Orders panel for a department-wide
               viewer, My Orders' otherwise
               (`components/hires/HireOrderExpansion.js`). Every change made
               there refreshes this page's hires silently. */
            <HireOrderExpansion
              deptWide={deptWide}
              list={list}
              stickyTop={headerTop}
              canManage={canManage}
              onExtend={openExtendModal}
              onOffHire={openOffHireModal}
              onReHire={openReHireModal}
              onViewOrder={(id) => continueWhenClean(() => handleViewOrder(id))}
              showRequestedBy={scope.showRequestedBy}
              ownSubDepartment={ownSubDepartment}
              columnWidths={hireColumnWidths}
              onColumnResize={setHireColumnWidth}
              onColumnResetWidth={resetHireColumnWidth}
              onRegionWidthChange={setHireTableRegionWidth}
              phone={isMobile}
              onSwipeTab={(id) => continueWhenClean(() => list.setTab(id))}
              onHiresChanged={silentRefetch}
              expandItem={expandItem}
              onExpandHandled={clearExpandItem}
              onDirtyItemChange={setDirtyItemId}
              continueWhenCleanRef={viewSwitchGuardRef}
              onExpandedItemChange={onExpandedItemChange}
              dirtyItemId={dirtyItemId}
            />
          ) : effectiveTab === 'kanban' ? (
            <KanbanView
              items={liveItems}
              onOpenHire={onOpenHireFromCard}
              openItemId={openHireItemId}
              canManage={canManage}
              onExtend={openExtendModal}
              onOffHire={openOffHireModal}
              onReHire={openReHireModal}
              onViewOrder={handleViewOrder}
              onCancelOrder={handleViewOrder}
            />
          ) : effectiveTab === 'gantt' ? (
            <GanttView
              /* Desktop: ONE page scroll, band pinned under the toolbar (the
                 Crew Gantt's layout). The phone keeps the capped chart. */
              pageScroll={!isMobile}
              stickyTop={headerTop}
              items={liveItems}
              onOpenHire={onOpenHireFromCard}
              openItemId={openHireItemId}
              canManage={canManage}
              onViewOrder={handleViewOrder}
              onExtend={openExtendModal}
              onOffHire={openOffHireModal}
              onReHire={openReHireModal}
              onDragComplete={handleDragComplete}
            />
          ) : effectiveTab === 'calendar' ? (
            <CalendarView
              events={liveEvents}
              onOpenHire={onOpenHireFromCard}
              openItemId={openHireItemId}
              onViewOrder={handleViewOrder}
              canManage={canManage}
              onExtend={openExtendModal}
              onOffHire={openOffHireModal}
              onReHire={openReHireModal}
            />
          ) : null}
        </>
      ) : null}

      {/* Action modal */}
      {modalItem && modalAction && (
        <HireActionModal
          item={modalItem}
          action={modalAction}
          onConfirm={modalAction === 'extend' ? handleExtend : modalAction === 're-hire' ? handleReHire : handleOffHire}
          onCancel={closeModal}
          loading={modalLoading}
          initialReturnDate={modalAction === 'off-hire' ? modalReturnDate : null}
          initialPriceChoice={modalAction === 'off-hire' ? modalPriceChoice : null}
        />
      )}

      {/* Keep-or-remove decision dialog (Task 8) — opened by `onNeedsDecision`
          when extend/shorten/change-start 409s with `adjustments_exist`. Layers
          ABOVE whichever surface triggered it (the action modal or a Gantt
          drag), the same way HireMoneyWarningDialog layers over the extend date
          form.
          🪤 Deliberately NOT `busy={modalLoading}`: `modalLoading` is `true` for
          the WHOLE recompute, including the entire span this dialog is open
          waiting on the human — passing it would disable Keep/Remove the
          instant the dialog appears (a disabled button does not receive clicks
          in a browser). Deciding closes the dialog SYNCHRONOUSLY, so there is
          no separate in-flight window of its own to guard. */}
      {pendingAdjustments != null && (
        <HireAdjustmentDecisionDialog
          adjustments={pendingAdjustments}
          onDecide={handleAdjustmentDecision}
        />
      )}
    </div>
  );
}
